import { Check } from "@untitledui/icons"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import { useEffect, useMemo, useRef, useState } from "react"

import { DOOR_LABELS } from "@/components/chat/DoorsTracker"
import TypingIndicator from "@/components/common/typing-indicator"
import {
  type Message,
  MessageItem,
} from "@/components/untitled-ui/application/messaging/messaging"
import { MS } from "@/constants/coaching-motion"
import type {
  CoachingExampleTurn,
  CoachingExampleTurns,
} from "@/utils/conversation-state-builder"

// An auto-playing worked example: one patient opens a door by saying what they
// feel and asking a real question; another talks themselves out of one. The good
// side ticks a door; the vague side opens nothing. A short caption names what to
// notice. Reuses the live chat's MessageItem bubbles + typing dots so it reads
// exactly like a real conversation. Pure presentation: it never touches the event
// stream or the demo seed (the dialogue is host-authored in intro.yml).

type Beat =
  | { kind: "header"; text: string }
  | { kind: "turn"; turn: CoachingExampleTurn }
  | { kind: "result"; opens?: string | null }
  | { kind: "divider" }
  | { kind: "caption"; lines: string[] }

function turnToMessage(turn: CoachingExampleTurn, id: string): Message {
  if (turn.speaker === "narrator") {
    return { id, sentAt: "", text: turn.text, user: {}, variant: "narrator" }
  }
  if (turn.speaker === "gp") {
    return {
      id,
      sentAt: "",
      text: turn.text,
      user: { name: "Dr Okafor" },
      variant: "bubble",
    }
  }
  // The patient is shown as "you" (right-aligned) so the trainee reads themselves
  // into the example.
  return {
    id,
    sentAt: "",
    text: turn.text,
    user: { me: true, name: "You" },
    variant: "bubble",
  }
}

export interface CoachingExamplePlayerProps {
  example: CoachingExampleTurns
}

export default function CoachingExamplePlayer({
  example,
}: CoachingExamplePlayerProps) {
  const reduceMotion = useReducedMotion()

  const beats = useMemo<Beat[]>(() => {
    const b: Beat[] = []
    b.push({ kind: "header", text: "One person opens a door" })
    example.good.turns.forEach((turn) => b.push({ kind: "turn", turn }))
    b.push({ kind: "result", opens: example.good.opens })
    b.push({ kind: "divider" })
    b.push({ kind: "header", text: "Another talks themselves out of one" })
    example.bad.turns.forEach((turn) => b.push({ kind: "turn", turn }))
    b.push({ kind: "result", opens: example.bad.opens })
    if (example.caption?.length) {
      b.push({ kind: "caption", lines: example.caption })
    }
    return b
  }, [example])

  const [revealed, setRevealed] = useState(0)
  const [typing, setTyping] = useState<null | "gp" | "you">(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  // Drive the reveal: speech bubbles are preceded by a typing beat; narration and
  // the result/divider/caption beats just dwell. Under reduced motion everything
  // is shown at once (no timers).
  useEffect(() => {
    if (reduceMotion) {
      if (revealed !== beats.length) setRevealed(beats.length)
      return
    }
    if (revealed >= beats.length) return

    const next = beats[revealed]
    let cancelled = false
    const advance = () => {
      if (cancelled) return
      setTyping(null)
      setRevealed((r) => r + 1)
    }

    let timer: number
    if (next.kind === "turn" && next.turn.speaker !== "narrator") {
      setTyping(next.turn.speaker === "gp" ? "gp" : "you")
      timer = window.setTimeout(advance, MS.exampleTyping)
    } else {
      const delay =
        next.kind === "divider"
          ? MS.exampleGap
          : Math.round(MS.exampleTurn * 0.7)
      timer = window.setTimeout(advance, delay)
    }
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [revealed, beats, reduceMotion])

  // Keep the latest revealed beat in view as the example unfolds.
  useEffect(() => {
    if (reduceMotion) return
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    })
  }, [revealed, typing, reduceMotion])

  const replay = () => {
    setTyping(null)
    setRevealed(reduceMotion ? beats.length : 0)
  }

  const done = revealed >= beats.length
  let turnSeq = 0

  return (
    <div className="mt-4 flex flex-col gap-3">
      <div
        ref={scrollRef}
        className="flex max-h-[38vh] flex-col gap-3 overflow-y-auto rounded-xl bg-gray-50 px-4 py-4 dark:bg-neutral-800/40"
      >
        <AnimatePresence initial={false}>
          {beats.slice(0, revealed).map((beat, i) => {
            if (beat.kind === "header") {
              return (
                <motion.p
                  key={`h${i}`}
                  initial={reduceMotion ? false : { opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-xs font-semibold tracking-wide text-gray-500 uppercase dark:text-gray-400"
                >
                  {beat.text}
                </motion.p>
              )
            }
            if (beat.kind === "turn") {
              turnSeq += 1
              return (
                <MessageItem
                  key={`t${i}`}
                  msg={turnToMessage(beat.turn, `t${i}-${turnSeq}`)}
                  showTimestamp={false}
                  showHeaderTimestamp={false}
                />
              )
            }
            if (beat.kind === "result") {
              const label = beat.opens ? DOOR_LABELS[beat.opens] : null
              return (
                <motion.div
                  key={`r${i}`}
                  initial={reduceMotion ? false : { opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex justify-center"
                >
                  {label ? (
                    <span className="flex items-center gap-1.5 rounded-full border border-green-200 bg-green-50 px-3 py-1 text-xs font-medium text-green-800 dark:border-green-800/60 dark:bg-green-900/20 dark:text-green-200">
                      <Check className="size-3.5 shrink-0 text-green-600 dark:text-green-400" />
                      A door opened — {label}
                    </span>
                  ) : (
                    <span className="rounded-full border border-gray-200 bg-gray-100 px-3 py-1 text-xs font-medium text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
                      No door opened
                    </span>
                  )}
                </motion.div>
              )
            }
            if (beat.kind === "divider") {
              return (
                <motion.div
                  key={`d${i}`}
                  initial={reduceMotion ? false : { opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="my-1 h-px w-full bg-gray-200 dark:bg-gray-700"
                />
              )
            }
            // caption
            return (
              <motion.div
                key={`c${i}`}
                initial={reduceMotion ? false : { opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-lg border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-neutral-900"
              >
                <div className="mb-1 text-xs font-semibold text-gray-900 dark:text-gray-100">
                  What to notice
                </div>
                <ul className="list-disc space-y-1 pl-5 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                  {beat.lines.map((line, idx) => (
                    <li key={idx}>{line}</li>
                  ))}
                </ul>
              </motion.div>
            )
          })}
        </AnimatePresence>

        {/* Typing dots for the speaker who's about to talk. */}
        {typing && (
          <div className={typing === "you" ? "flex justify-end" : ""}>
            <TypingIndicator />
          </div>
        )}
      </div>

      {done && (
        <button
          onClick={replay}
          className="self-center text-xs font-medium text-gray-500 underline-offset-2 hover:text-gray-700 hover:underline dark:text-gray-400 dark:hover:text-gray-200"
        >
          Replay
        </button>
      )}
    </div>
  )
}
